import React, { useCallback, useEffect, useRef } from "react"
import {
	GoogleMap,
	LoadScript,
	Marker,
	OverlayView,
	Polygon,
	Polyline,
} from "@react-google-maps/api"
import { Duration } from "luxon"

type LatLngLiteral = google.maps.LatLngLiteral

type MarkerButton = {
	label: string
	color?: string
	onClick: () => void
}

type MarkerData = {
	lat: number
	lng: number
	onClick?: (marker: MarkerData) => void
	icon?: string | google.maps.Icon | google.maps.Symbol
	label?: string | google.maps.MarkerLabel
	order?: any
	overlay?: React.ReactNode // Custom overlay content to display on hover
	buttons?: MarkerButton[] // Action buttons rendered in the default overlay
}

interface GoogleMapsProps {
	overlay?: boolean // If true, use OverlayView for custom marker info
	overlaySize?: "small" | "medium" | "large" // Size of the overlay (default: large)
	overlayTrigger?: "hover" | "click" // How to trigger overlay display (default: hover)
	showOverlayCloseButton?: boolean // If true, show a close button on click overlays
	loadScript?: boolean
	center?: LatLngLiteral
	boundingBox?: {
		south: number
		west: number
		north: number
		east: number
	}
	// Off by default — `boundingBox` otherwise only fits once, on load. Several
	// callers recompute it on every render (selection/filter changes) and
	// rely on that *not* moving the map underneath the user, so this only
	// re-fits on later changes when a caller explicitly opts in.
	refitOnBoundsChange?: boolean
	zoom?: number
	markers?: MarkerData[]
	polygons?: {
		paths: { lat: number; lng: number }[]
		fillColor: string
		fillOpacity: number
		strokeColor: string
		strokeOpacity: number
		strokeWeight: number
	}[]
	polylines?: {
		path: { lat: number; lng: number }[]
		strokeColor?: string
		strokeOpacity?: number
		strokeWeight?: number
		// Repeats a small arrow along the whole path, so the travel direction
		// reads at a glance instead of just from the line's two endpoints.
		showDirection?: boolean
	}[]
	onClick?: {
		address?: boolean
		function: (location: {
			lat: number
			lng: number
			address?: string
			postalCode?: string
			city?: string
			country?: string
		}) => void
	}
	mapRef?: React.MutableRefObject<google.maps.Map | null>
	mapContainerStyle?: React.CSSProperties
	options?: google.maps.MapOptions
	onMapLoad?: (map: google.maps.Map) => void
}

const defaultCenter: LatLngLiteral = { lat: 38.72152, lng: -9.142161 }

const defaultMapContainerStyle: React.CSSProperties = {
	width: "100%",
	height: "400px",
	borderRadius: 5,
}

// 🌍 Default options for Google Maps component
const defaultOptions: google.maps.MapOptions = {
	streetViewControl: false,
	scaleControl: false,
	mapTypeControl: true,
	panControl: true,
	zoomControl: true,
	rotateControl: true,
	fullscreenControl: true,
	cameraControl: false,
	gestureHandling: "auto",
	// ✋ Gesture handling mode
	//   🤝 "cooperative" - Scroll to zoom only works after user interacts with the map.
	//   👆 "greedy"      - All touch gestures and scroll events pan or zoom the map.
	//   🚫 "none"        - The map cannot be panned or zoomed by user gestures.
	//   🤖 "auto"        - Gesture handling is automatically set based on device and context.
}

export const GoogleMaps: React.FC<GoogleMapsProps> = ({
	overlay = false, // If true, use OverlayView for custom marker info
	overlaySize = "large", // Size of the overlay (default: large)
	overlayTrigger = "hover", // Default to hover
	showOverlayCloseButton = true,
	loadScript = false,
	center = defaultCenter,
	boundingBox,
	refitOnBoundsChange = false,
	zoom = 12,
	markers = [],
	polygons = [],
	polylines = [],
	onClick,
	mapRef, // External ref to hold the map instance
	mapContainerStyle = defaultMapContainerStyle,
	options,
	onMapLoad,
}) => {
	// Create a ref to hold the map instance
	const internalMapRef = useRef<google.maps.Map | null>(null)
	const effectiveMapRef = mapRef || internalMapRef // Pick the one to use internally

	// `onLoad` below only fits bounds once, when the map is first created —
	// opt in via `refitOnBoundsChange` if a later `boundingBox` change (e.g.
	// a new route selection) should also move the map, instead of leaving it
	// stuck wherever it was.
	useEffect(() => {
		if (refitOnBoundsChange && effectiveMapRef.current && boundingBox) {
			effectiveMapRef.current.fitBounds(boundingBox)
		}
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [boundingBox])

	// Set ref and variables
	const lastGeocodeTimeRef = useRef<number>(0)
	const GEOCLICK_DEBOUNCE_MS = 1500 // 1.5 seconds between geocode requests
	const onMouseOutDelay = 200

	const [hoveredMarker, setHoveredMarker] = React.useState<any>(null)
	const [clickedMarker, setClickedMarker] = React.useState<any>(null)
	const [isHoveringOverlay, setIsHoveringOverlay] = React.useState(false)
	const isHoveringOverlayRef = useRef(false)
	const [copiedOrderId, setCopiedOrderId] = React.useState<string | null>(null)
	const overlayClickedRef = useRef(false)

	// Keep the ref in sync with state to use in setTimeout
	React.useEffect(() => {
		isHoveringOverlayRef.current = isHoveringOverlay
	}, [isHoveringOverlay])

	const handleMapClick = useCallback(
		(e: google.maps.MapMouseEvent) => {
			// Handle close overlayTrigger click overlays
			if (overlayTrigger === "click" && clickedMarker) {
				// Set timeout to allow overlay onClick to set the ref first
				setTimeout(() => {
					const isClickOnOverlay = overlayClickedRef.current

					if (isClickOnOverlay) {
						overlayClickedRef.current = false // Reset for next time
					} else {
						setClickedMarker(null)
					}
				}, 0)
				return
			}

			if (!onClick || !e.latLng) return

			if (onClick.address) {
				const now = Date.now()
				if (now - lastGeocodeTimeRef.current < GEOCLICK_DEBOUNCE_MS) {
					return
				}
				lastGeocodeTimeRef.current = now

				const geocoder = new window.google.maps.Geocoder()
				geocoder.geocode({ location: e.latLng }, (results, status) => {
					if (status === "OK" && results && results[0]) {
						onClick.function({
							lat: e.latLng!.lat(),
							lng: e.latLng!.lng(),
							address: results[0].formatted_address,
							postalCode: results[0].address_components.find((component: any) =>
								component.types.includes("postal_code")
							)?.long_name,
							city: results[0].address_components.find(
								(component: any) =>
									component.types.includes("locality") ||
									component.types.includes("administrative_area_level_2")
							)?.long_name,
							country: results[0].address_components.find((component: any) =>
								component.types.includes("country")
							)?.long_name,
						})
					}
				})
			} else {
				onClick.function({
					lat: e.latLng.lat(),
					lng: e.latLng.lng(),
				})
			}
		},
		[onClick, clickedMarker, overlayTrigger]
	)

	// The active marker for the general overlay (orderId/date/slot/etc info panel)
	const generalOverlayMarker =
		overlayTrigger === "click" ? clickedMarker : hoveredMarker

	const mapContent = (
		<GoogleMap
			mapContainerStyle={mapContainerStyle}
			center={center}
			zoom={zoom}
			options={{ ...defaultOptions, ...(options || {}) }}
			onClick={handleMapClick}
			onLoad={(map) => {
				if (effectiveMapRef) effectiveMapRef.current = map

				if (boundingBox) {
					map.fitBounds(boundingBox)
				}

				onMapLoad?.(map)
			}}
		>
			{markers?.map((marker, index) => {
				const activeMarker =
					overlayTrigger === "click" ? clickedMarker : hoveredMarker

				return (
					<React.Fragment key={index}>
						<Marker
							position={{ lat: marker.lat, lng: marker.lng }}
							icon={marker.icon || undefined}
							label={marker.label || undefined}
							onClick={() => {
								marker.onClick?.(marker)
								if (overlayTrigger === "click") {
									setClickedMarker(
										clickedMarker?.index === index
											? null
											: {
													index,
													customOverlayActive: !!marker.overlay,
													lat: marker.lat,
													lng: marker.lng,
													orderId: marker.order?.orderId || "N/A",
													date: marker.order?.orderFlow?.deliveryDate
														? new Date(
																marker.order.orderFlow.deliveryDate
															).toLocaleDateString()
														: "N/A",
													slot: marker.order?.slot
														? `${Duration.fromMillis(marker.order.slot.from).toFormat("hh:mm")} - ${Duration.fromMillis(marker.order.slot.to).toFormat("hh:mm")}`
														: "N/A",
													clientName: marker.order?.user || "N/A",
													address:
														marker.order?.formattedAddress?.address || "N/A",
													overlay: marker.overlay,
													buttons: marker.buttons,
												}
									)
								}
							}}
							onMouseOver={() => {
								if (overlayTrigger === "hover") {
									setHoveredMarker({
										index,
										customOverlayActive: !!marker.overlay,
										lat: marker.lat,
										lng: marker.lng,
										orderId: marker.order?.orderId || "N/A",
										date: marker.order?.orderFlow.deliveryDate
											? new Date(
													marker.order.orderFlow.deliveryDate
												).toLocaleDateString()
											: "N/A",
										slot: marker.order?.slot
											? `${Duration.fromMillis(
													marker.order?.slot.from
												).toFormat(
													"hh:mm"
												)} - ${Duration.fromMillis(marker.order?.slot.to).toFormat("hh:mm")}`
											: "N/A",
										clientName: marker.order?.user || "N/A",
										address: marker.order?.formattedAddress?.address || "N/A",
									})
								}
							}}
							onMouseOut={() => {
								if (overlayTrigger === "hover") {
									// Delay hiding to allow mouse to move into overlay
									setTimeout(() => {
										if (!isHoveringOverlayRef.current) setHoveredMarker(null)
									}, onMouseOutDelay)
								}
							}}
						/>
						{/* Custom marker overlay */}

						{marker.overlay &&
							(activeMarker?.index === index ||
								(isHoveringOverlay && activeMarker?.index === index)) && (
								<OverlayView
									position={{ lat: marker.lat, lng: marker.lng }}
									mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
								>
									<div
										className="map-overlay-container"
										onMouseEnter={() => {
											if (overlayTrigger === "hover") {
												setIsHoveringOverlay(true)
												if (effectiveMapRef.current) {
													effectiveMapRef.current.setOptions({
														draggable: false,
													}) // ❌ disable drag
												}
											}
										}}
										onMouseLeave={() => {
											if (overlayTrigger === "hover") {
												setIsHoveringOverlay(false)
												setHoveredMarker(null)
												if (effectiveMapRef.current) {
													effectiveMapRef.current.setOptions({
														draggable: true,
													}) // ✅ re-enable drag
												}
											}
										}}
										onClick={(e) => {
											if (overlayClickedRef.current) return // Prevent multiple rapid clicks
											overlayClickedRef.current = true
											e.stopPropagation()
											// Reset after short delay
											setTimeout(() => {
												overlayClickedRef.current = false
											}, 100)
										}}
										style={{
											background: "white",
											padding: "4px 8px",
											borderRadius: "4px",
											boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
											fontSize: "14px",
											whiteSpace: "nowrap",
											transform: "translate(-50%, -110%)", // ⬅️ center horizontally, move above marker
											position: "absolute",
											cursor:
												overlayTrigger === "click" ? "pointer" : "default",
										}}
									>
										{marker.overlay}
										{overlayTrigger === "click" && showOverlayCloseButton && (
											<div
												style={{
													position: "absolute",
													top: 4,
													right: 4,
													cursor: "pointer",
													fontSize: 16,
													fontWeight: "bold",
													color: "#999",
												}}
												onClick={(e) => {
													e.stopPropagation()
													setClickedMarker(null)
												}}
											>
												×
											</div>
										)}
									</div>
								</OverlayView>
							)}
					</React.Fragment>
				)
			})}
			{/* Overlay info on marker */}
			{overlay &&
				generalOverlayMarker &&
				!generalOverlayMarker.customOverlayActive && (
					<OverlayView
						position={{
							lat: generalOverlayMarker.lat,
							lng: generalOverlayMarker.lng,
						}}
						mapPaneName={OverlayView.OVERLAY_MOUSE_TARGET}
					>
						<div
							className="map-overlay-container"
							onMouseEnter={() => {
								if (overlayTrigger === "hover") {
									setIsHoveringOverlay(true)
									if (effectiveMapRef.current) {
										effectiveMapRef.current.setOptions({ draggable: false }) // ❌ disable drag
									}
								}
							}}
							onMouseLeave={() => {
								if (overlayTrigger === "hover") {
									setIsHoveringOverlay(false)
									setHoveredMarker(null)
									if (effectiveMapRef.current) {
										effectiveMapRef.current.setOptions({ draggable: true }) // ✅ re-enable drag
									}
								}
							}}
							onClick={(e) => {
								if (overlayTrigger === "click") {
									overlayClickedRef.current = true
									e.stopPropagation()
									setTimeout(() => {
										overlayClickedRef.current = false
									}, 100)
								}
							}}
							style={{
								background: "white",
								padding: "4px 8px",
								borderRadius: "4px",
								boxShadow: "0 1px 4px rgba(0,0,0,0.3)",
								fontSize: "14px",
								whiteSpace: "nowrap",
								transform: "translate(-50%, -110%)", // ⬅️ center horizontally, move above marker
								position: "absolute",
								maxWidth:
									overlaySize === "small"
										? 250
										: overlaySize === "medium"
											? 300
											: "",
							}}
						>
							{overlayTrigger === "click" && showOverlayCloseButton && (
								<div
									style={{
										position: "absolute",
										top: 4,
										right: 4,
										cursor: "pointer",
										fontSize: 16,
										fontWeight: "bold",
										color: "#999",
									}}
									onClick={(e) => {
										e.stopPropagation()
										setClickedMarker(null)
									}}
								>
									×
								</div>
							)}
							<div
								style={{
									display: "flex",
									flexDirection: "column",
									gap: 4,
									minWidth: generalOverlayMarker.buttons?.length > 0 ? 0 : 220,
									width: "100%",
								}}
							>
								<div style={{ fontWeight: 600, fontSize: 15, marginBottom: 4 }}>
									Order ID:{" "}
									<span style={{ color: "#1C70D9" }}>
										{generalOverlayMarker.orderId}
									</span>
									<button
										style={{
											marginLeft: 8,
											padding: "2px 8px",
											fontSize: 12,
											borderRadius: 3,
											border: "1px solid #ddd",
											background: "#f5f5f5",
											cursor: "pointer",
											position: "relative",
										}}
										onClick={async (e) => {
											e.stopPropagation()
											await navigator.clipboard.writeText(
												generalOverlayMarker.orderId
											)
											setCopiedOrderId(generalOverlayMarker.orderId)
											setTimeout(() => setCopiedOrderId(null), 3000)
										}}
										title="Copy Order ID"
									>
										{copiedOrderId === generalOverlayMarker.orderId
											? "Copied!"
											: "Copy"}
									</button>
								</div>
								<div>
									<span style={{ fontWeight: 500 }}>Date:</span>{" "}
									{generalOverlayMarker.date || (
										<span style={{ color: "#aaa" }}>N/A</span>
									)}
								</div>
								<div>
									<span style={{ fontWeight: 500 }}>Slot:</span>{" "}
									{generalOverlayMarker.slot || (
										<span style={{ color: "#aaa" }}>N/A</span>
									)}
								</div>
								<div>
									<span style={{ fontWeight: 500 }}>Client:</span>{" "}
									{generalOverlayMarker.clientName || (
										<span style={{ color: "#aaa" }}>N/A</span>
									)}
								</div>
								<div
									style={{
										display: "block",
										width: "100%",
										wordBreak: "break-word",
										whiteSpace: "normal",
									}}
								>
									<span style={{ fontWeight: 500 }}>Address: </span>
									{generalOverlayMarker.address || (
										<span style={{ color: "#aaa" }}>N/A</span>
									)}
								</div>
								{generalOverlayMarker.buttons?.length > 0 && (
									<div
										style={{
											display: "grid",
											gridTemplateColumns: "1fr 1fr",
											gap: 4,
											marginTop: 8,
										}}
									>
										{generalOverlayMarker.buttons.map(
											(btn: MarkerButton, i: number) => (
												<button
													key={i}
													style={{
														padding: "2px 6px",
														fontSize: 11,
														color: btn.color || "#1890ff",
														border: `1px solid ${btn.color || "#1890ff"}`,
														borderRadius: 3,
														background: "white",
														cursor: "pointer",
														textAlign: "center",
														overflow: "hidden",
														textOverflow: "ellipsis",
														whiteSpace: "nowrap",
														minWidth: 0,
														...(i === generalOverlayMarker.buttons.length - 1 &&
														generalOverlayMarker.buttons.length % 2 !== 0
															? { gridColumn: "1 / -1" }
															: {}),
													}}
													onClick={(e) => {
														e.stopPropagation()
														btn.onClick()
														setClickedMarker(null)
													}}
												>
													{btn.label}
												</button>
											)
										)}
									</div>
								)}
							</div>
						</div>
					</OverlayView>
				)}
			{polygons?.map((polygon, index) => (
				<Polygon
					key={index}
					paths={polygon.paths}
					options={{
						fillColor: polygon.fillColor || "#212ff3",
						fillOpacity: polygon.fillOpacity || 0,
						strokeColor: polygon.strokeColor || "#1C70D9",
						strokeOpacity: polygon.strokeOpacity || 1,
						strokeWeight: polygon.strokeWeight || 2.5,
						clickable: false,
						editable: false,
						draggable: false,
						zIndex: 1,
					}}
				/>
			))}
			{polylines?.map((polyline, index) => (
				<Polyline
					key={index}
					path={polyline.path}
					options={{
						strokeColor: polyline.strokeColor || "#808080",
						strokeOpacity: polyline.strokeOpacity ?? 0.8,
						strokeWeight: polyline.strokeWeight ?? 6,
						clickable: false,
						zIndex: 1,
						icons: polyline.showDirection
							? [
									{
										icon: {
											path: window.google.maps.SymbolPath.FORWARD_CLOSED_ARROW,
											scale: 3.5,
											fillColor: "#ffffff",
											fillOpacity: 1,
											strokeColor: "#333333",
											strokeWeight: 1.2,
										},
										offset: "0",
										repeat: "70px",
									},
								]
							: undefined,
					}}
				/>
			))}
		</GoogleMap>
	)

	return (
		<>
			<style>
				{`
			.gm-bundled-control-on-bottom > div {
				transform: scale(0.75);
				transform-origin: bottom right;
			}
			.gm-style .gm-style-mtc-bbw {
				transform: scale(0.75);
				transform-origin: top left;
			}
			.gm-fullscreen-control {
				transform: scale(0.9);
				transform-origin: top right;
			}
		`}
			</style>
			{loadScript ? (
				<LoadScript
					googleMapsApiKey={process.env.REACT_APP_GOOGLE_MAPS_API_KEY as string}
				>
					{mapContent}
				</LoadScript>
			) : (
				mapContent
			)}
		</>
	)
}
